import {
  DocumentAnyData,
  DocumentCreateData,
  DocumentUpdateData, isCreateDocumentValues,
  isUpdateDocumentValues, NoteDocumentCreateFormValues, NoteDocumentUpdateFormValues,
  NotesDocument,
  PreviousDocument, PreviousDocumentCreateFormValues, PreviousDocumentUpdateFormValues,
  SupportingDocument, SupportingDocumentCreateFormValues, SupportingDocumentUpdateFormValues,
  TransportDocument, TransportDocumentCreateFormValues, TransportDocumentUpdateFormValues,
} from 'features/declaration/models/schemas/documentSchemas'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import {
  DocumentFileSave,
  DocumentCreate,
  NoteDocumentCreate,
  PreviousDocumentCreate,
  SupportingDocumentCreate,
  TransportDocumentCreate,
  DocumentUpdate,
  TransportDocumentUpdate,
  SupportingDocumentUpdate,
  PreviousDocumentUpdate,
  NoteDocumentUpdate,
  DocumentSaveBatch,
} from './request'
import { CreateForm, UpdateForm } from '../../../../shared/rhf/models/types'

export function toFormData(fileType: FileType): FormData {
  const formData = new FormData()
  formData.append('filename', fileType.fileName ?? '')
  formData.append('mimeType', fileType.mimeType)
  formData.append('sortOrder', `${fileType.sequenceNumber}`)
  formData.append('documentId', `${fileType.documentId}`)
  formData.append('multipartFile', fileType.fileBlob as Blob, fileType.fileName ?? '')

  return formData
}

function toDocumentFileSave(file: FileType): DocumentFileSave {
  return {
    id: file.id ?? null,
    fileName: file.fileName ?? '',
    documentId: file.documentId ?? null,
    mimeType: file.mimeType ?? '',
    sequenceNumber: file.sequenceNumber,
    deleted: file.deleted ?? false,
    uuid: file.uuid ?? null,
    file: file.file ?? null,
    fileBlob: file.fileBlob ?? null,
    url: file.url ?? null,
  }
}

function toTransportDocumentCreate(document: CreateForm<TransportDocument>): TransportDocumentCreate {
  return {
    type: 'TRANSPORT',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toTransportDocumentUpdate(document: UpdateForm<TransportDocument>): TransportDocumentUpdate {
  return {
    id: document.id,
    type: 'TRANSPORT',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toPreviousDocumentCreate(document: CreateForm<PreviousDocument>): PreviousDocumentCreate {
  return {
    type: 'PREVIOUS',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    complementOfInformation: document.complementOfInformation,
    transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
    procedureType: document.procedureType,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toPreviousDocumentUpdate(document: UpdateForm<PreviousDocument>): PreviousDocumentUpdate {
  return {
    id: document.id,
    type: 'PREVIOUS',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    complementOfInformation: document.complementOfInformation,
    transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
    procedureType: document.procedureType,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toSupporingDocumentCreate(document: CreateForm<SupportingDocument>): SupportingDocumentCreate {
  return {
    type: 'SUPPORTING',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    complementOfInformation: document.complementOfInformation,
    documentLineNumber: document.documentLineNumber,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}
function toSupporingDocumentUpdate(document: UpdateForm<SupportingDocument>): SupportingDocumentUpdate {
  return {
    id: document.id,
    type: 'SUPPORTING',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    complementOfInformation: document.complementOfInformation,
    documentLineNumber: document.documentLineNumber,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toNoteDocumentCreate(document: CreateForm<NotesDocument>): NoteDocumentCreate {
  return {
    type: 'NOTE',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    transitOperationId: document.transitOperationId,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function toNoteDocumentUpdate(document: UpdateForm<NotesDocument>): NoteDocumentUpdate {
  return {
    id: document.id,
    type: 'NOTE',
    sequenceNumber: document.sequenceNumber,
    documentType: document.documentType,
    referenceNumber: document.referenceNumber,
    transitOperationId: document.transitOperationId,
    deleted: document.deleted ?? false,
    files: document.files.map(toDocumentFileSave),
  }
}

function isTransportDocumentCreateFormValues(
  formValues: DocumentCreateData,
): formValues is TransportDocumentCreateFormValues {
  return formValues.type === 'TRANSPORT'
}

function isTransportDocumentUpdateFormValues(
  formValues: DocumentUpdateData,
): formValues is TransportDocumentUpdateFormValues {
  return formValues.type === 'TRANSPORT'
}

function isPreviousDocumentCreateFormValues(
  formValues: DocumentCreateData,
): formValues is PreviousDocumentCreateFormValues {
  return formValues.type === 'PREVIOUS'
}

function isPreviousDocumentUpdateFormValues(
  formValues: DocumentUpdateData,
): formValues is PreviousDocumentUpdateFormValues {
  return formValues.type === 'PREVIOUS'
}

function isSupportingDocumentCreateFormValues(
  formValues: DocumentCreateData,
): formValues is SupportingDocumentCreateFormValues {
  return formValues.type === 'SUPPORTING'
}

function isSupportingDocumentUpdateFormValues(
  formValues: DocumentUpdateData,
): formValues is SupportingDocumentUpdateFormValues {
  return formValues.type === 'SUPPORTING'
}

function isNoteDocumentCreateFormValues(
  formValues: DocumentCreateData,
): formValues is NoteDocumentCreateFormValues {
  return formValues.type === 'NOTE'
}

function isNoteDocumentUpdateFormValues(
  formValues: DocumentUpdateData,
): formValues is NoteDocumentUpdateFormValues {
  return formValues.type === 'NOTE'
}

export function toDocumentCreate(document: DocumentCreateData): DocumentCreate {
  const documentType = document.type

  if (isTransportDocumentCreateFormValues(document)) return toTransportDocumentCreate(document)
  if (isPreviousDocumentCreateFormValues(document)) return toPreviousDocumentCreate(document)
  if (isSupportingDocumentCreateFormValues(document)) return toSupporingDocumentCreate(document)
  if (isNoteDocumentCreateFormValues(document)) return toNoteDocumentCreate(document)

  throw new Error(`Unsupported document type: ${documentType}`)
}

export function toDocumentUpdate(document: DocumentUpdateData): DocumentUpdate {
  const documentType = document.type

  if (isTransportDocumentUpdateFormValues(document)) return toTransportDocumentUpdate(document)
  if (isPreviousDocumentUpdateFormValues(document)) return toPreviousDocumentUpdate(document)
  if (isSupportingDocumentUpdateFormValues(document)) return toSupporingDocumentUpdate(document)
  if (isNoteDocumentUpdateFormValues(document)) return toNoteDocumentUpdate(document)

  throw new Error(`Unsupported document type: ${documentType}`)
}

export function toDocumentSaveBatch(documents: DocumentAnyData[]): DocumentSaveBatch {
  const creates: DocumentCreate[] = []
  const updates: DocumentUpdate[] = []
  for (const document of documents) {
    if (isCreateDocumentValues(document)) {
      creates.push(toDocumentCreate(document))
    } else {
      updates.push(toDocumentUpdate(document))
    }
  }

  return { creates, updates }
}

export function toDocumentUpdates(documents: DocumentAnyData[]): DocumentUpdate[] {
  const updates: DocumentUpdate[] = []
  for (const document of documents) {
    if (isUpdateDocumentValues(document)) {
      updates.push(toDocumentUpdate(document))
    }
  }

  return updates
}
